// 处理用户密码相关
import { Ref, ref } from '@vue/composition-api';
import { encode } from 'js-base64';
import i18n, { i18n_t } from '@/locales/i18n';
import tips from '@/renderer/scripts/utils/tips';
import smh, { SMH } from '@/renderer/scripts/smh';
import { copyText2Clipboard } from '@/renderer/scripts/utils/tools/index';
type UpdatePasswordParams = {
  originalAccountPassword: string;
  accountPassword: string;
};
type CopyResetPasswordUrlParams = {
  type: 'createUser' | 'resetPassword' | 'copyInviteUrl';
  url: string;
  nickname: string;
  accountName: string;
  accountPassword?: string;
  formatTime?: string;
};
type Handler = {
  corpList: Ref<any[]>;
  getCorpList: () => Promise<void>;
  getResetToken: (userId: number) => Promise<SMH.ResetToken | null>;
  createResetToken: (userId: number) => Promise<string>;
  getResetTokenDetail: (token: string) => Promise<SMH.GetResetTokenDetailData | null>;
  updatePassword: (params: UpdatePasswordParams) => Promise<boolean>;
  resetNewPassword: (params: SMH.ResetNewPasswordParams) => Promise<boolean>;
  copyResetPasswordUrl: (params: CopyResetPasswordUrlParams) => void;
};
export default function submitHandler(): Handler {
  const corpList = ref([]);
  const methods = {
    // 获取机构列表
    async getCorpList() {
      try {
        const { data } = await smh.api.getCorpList();
        corpList.value = data.organizationList;
      } catch (e: any) {
        tips.error(e);
      }
    },
    // 查询重置token
    async getResetToken(resetUserId: number) {
      try {
        const { data } = await smh.userAPI.getResetToken({
          resetUserId,
        });
        const { resetToken } = data;
        return resetToken;
      } catch (e) {
        // 没查询到不用tip提示
        return null;
      }
    },
    // 查询重置token详情
    async getResetTokenDetail(resetPasswordToken: string) {
      try {
        const { data } = await smh.userAPI.getResetTokenDetail({
          resetPasswordToken,
        });
        return data;
      } catch (e) {
        tips.error(e as MsgError);
        return null;
      }
    },
    // 创建重置token
    async createResetToken(userId: number) {
      try {
        const { data } = await smh.userAPI.createResetToken({
          userId,
        });
        const { resetToken } = data;
        return resetToken;
      } catch (e) {
        tips.error(e as MsgError);
        return '';
      }
    },
    // 重置新密码
    async resetNewPassword(params: SMH.ResetNewPasswordParams) {
      try {
        await smh.userAPI.resetNewPassword(params);
        tips.success(i18n_t('密码重置成功'));
        return true;
      } catch (e) {
        tips.error(e as MsgError);
        return false;
      }
    },
    // 修改自己的密码
    async updatePassword(params: UpdatePasswordParams) {
      try {
        await smh.userAPI.updatePassword({
          originalAccountPassword: encode(params.originalAccountPassword),
          accountPassword: encode(params.accountPassword),
        });
        tips.success(i18n_t('修改成功'));
        return true;
      } catch (e) {
        tips.error(e as MsgError);
        return false;
      }
    },
    // 复制重置密码的链接
    copyResetPasswordUrl(params: CopyResetPasswordUrlParams) {
      const { type, url = '', nickname = '', accountName = '', accountPassword = '', formatTime = '' } = params;
      let copyContent = '';
      // 不同type对应的场景下 文案略有不同
      if (type === 'createUser') {
        // 新建用户完成弹窗里
        copyContent = `${i18n_t('{nickname}您好，您的网盘账号已创建，账号信息如下，请尽快登录修改初始密码', {
          nickname,
        })}\n${i18n_t('登录地址: {url}', { url })}\n${i18n_t('登录账号: {accountName}', {
          accountName,
        })}\n${i18n_t('初始密码: {accountPassword}', { accountPassword })}`;
      } else if (type === 'resetPassword') {
        // 用户编辑弹窗里重置密码
        copyContent = `${i18n_t(
          '{nickname}您好，您的网盘账号已创建，账号信息如下，请在【{formatTime}】前登录修改密码',
          {
            nickname,
            formatTime,
          },
        )}\n${i18n_t('修改地址: {url}', { url })}\n${i18n_t('登录账号: {accountName}', { accountName })}`;
      } else if (type === 'copyInviteUrl') {
        // 用户列表里邀请用户去重置密码
        copyContent = `${i18n_t('{nickname}您好，您的网盘账号已创建，账号信息如下，请尽快登录修改初始密码', {
          nickname,
        })}\n${i18n_t('修改地址: {url}', { url })}\n${i18n_t('登录账号: {accountName}', { accountName })}`;
      }
      copyText2Clipboard(copyContent)
        .then(() => {
          tips.success(i18n_t('复制成功'));
        })
        .catch(() => {
          tips.error(i18n_t('复制失败'));
        });
    },
  };
  return {
    corpList,
    ...methods,
  };
}
